import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit } from '@angular/core';
import { User, getUserDisplayName } from '@core/core.state';
import { Store, select } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { filter } from 'rxjs';
import * as CoreActions from '@core/core.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectActiveUsers } from '@core/core.selectors';
import { ResourceStatuses } from '@platform/shared/resource.state';
import { DialogV2Service } from '@platform/dialog/dialog-v2.service';
import { UserSelectionDialogComponent } from '@platform/components/user-selection-dialog/user-selection-dialog.component';
import { ConfirmDialogComponent } from '@platform/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-user-deputies-editor',
  templateUrl: './user-deputies-editor.component.html',
  styleUrls: ['./user-deputies-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserDeputiesEditorComponent implements OnInit {
  @Input() userDeputies: User[] = [];
  @Input() userId: string;

  private activeUsers: User[] = [];

  constructor(
    private store$: Store<RootState>,
    private dialogService: DialogV2Service,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.store$
      .pipe(
        select(selectActiveUsers),
        filter((users) => users.status === ResourceStatuses.SUCCEEDED && !!users.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.activeUsers = users.data));
  }

  public openAddDeputyDialog() {
    this.dialogService
      .open(UserSelectionDialogComponent, {
        data: {
          titleT: 'users.user-profile.select-deputy',
          availableUsers: this.getAvailableDeputies(),
        },
      })
      .afterClosed()
      .subscribe((data) => {
        if (data?.isConfirmed && data.selectedUser) {
          this.store$.dispatch(CoreActions.addUserDeputy({ userId: this.userId, deputyId: data.selectedUser.id }));
        }
      });
  }

  public openDeleteDeputyDialog(deputy: User) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        data: {
          titleTranslationKey: 'users.user-profile.remove-deputy.title',
          textTranslationKey: 'users.user-profile.remove-deputy.really-remove',
        },
      })
      .afterClosed()
      .subscribe((data) => {
        if (data?.isConfirmed) {
          this.store$.dispatch(CoreActions.removeUserDeputy({ userId: this.userId, deputyId: deputy.id }));
        }
      });
  }

  public getUserDisplayName(user: User): string {
    return getUserDisplayName(user);
  }

  private getAvailableDeputies(): User[] {
    const addedDeputies = this.userDeputies.map((u) => u.id);

    return this.activeUsers.filter((user) => user.isInternal && user.id !== this.userId && addedDeputies.indexOf(user.id) < 0);
  }
}
